/**
 * 클라이언트·서버 공유 타입 (단일 정의).
 * 출처: docs/build/02 (데이터·API·프로토콜), docs/build/07 (랠리).
 * ⚠️ 불변식: 비회원·무수집. 개인식별정보 없음(닉네임은 비식별).
 */

// ── 히어로(아바타) — FR-HERO, docs/build/02 §6.5 ──────────────
// explorer_* = 완성형 프리셋 캐릭터(hero.base.<id> 이미지). "custom" = 직접 꾸미기(파츠·색상 레이어드, 프리셋 이미지 없음 → 절차적/파츠 렌더).
/**
 * 완성 캐릭터 id. explorer_* = 기본 제공, "custom" = 직접 꾸미기(파츠 레이어드),
 * 그 밖의 값 = **운영자가 만든 캐릭터**(hero.base.<id> 그림이 있으면 그걸로 그린다).
 *
 * 예전엔 다섯 개짜리 유니온이었는데 **카탈로그와 이미 어긋나 있었고**(f~i 누락),
 * 운영자가 캐릭터를 만들 수 있게 되면서 코드가 목록을 쥐고 있을 이유가 사라졌다.
 * 값이 옳은지는 isBase 가 **모양으로** 본다(불변식 7) — 거기 설명이 있다.
 */
export type HeroBase = string; // IP 캐릭터(그레그·병아리)와 구분(S2-1)

export interface SavedHero {
  version: number; // 스키마 버전(마이그레이션·폴백)
  code?: string; // 익명 히어로 코드(서버 동기화 시). 기기 변경 복원 키
  base: HeroBase;
  parts: string; // 파츠 조합 "b2_h1_a3" — 로드 시 화이트리스트 검증(FR-HERO-15)
  palette?: string;
  nickname?: string; // 선택. 비식별. 클라+서버 금칙 필터
  updatedAt: string; // ISO8601
}

export const HERO_STORAGE_KEY = "gc_hero_v1";
/**
 * 🔴 `HERO_CODE_ALPHABET` 은 **`hero.ts` 로 옮겼다**(아래 `export * from "./hero"` 로 그대로 나간다).
 *
 * 여기(배럴)에 두면 **순환 참조**가 된다: `classroom.ts` 가 이 상수를 쓰려고 `./index` 를
 * 들여오는데, `./index` 는 다시 `classroom` 을 내보낸다. 그러면 **들여오는 순서에 따라**
 * 상수가 `undefined` 인 채로 함수가 돌아간다 — 실측: `hero.ts` 를 먼저 들여오면
 * `parseHeroCode("N9QSRG")` 가 **`null`** 이 됐다(멀쩡한 코드인데 아이가 못 되살린다).
 *
 * 값은 **정의한 파일에서** 가져온다. 배럴은 바깥으로 내보내는 창구일 뿐이다.
 */

// ── 콘텐츠 메타(맵 오브젝트) — docs/build/02 §3 ───────────────
export type ObjectType = "video" | "link" | "card" | "portal" | "npc" | "store" | "decor";
export type ZoneId = "lobby" | "book" | "gyeongju" | "goods" | "event";

export interface ContentObject {
  id: string;
  zone: ZoneId;
  type: ObjectType;
  title_ko: string;
  title_en?: string;
  url?: string;
  video_youtube?: string; // 임베드는 youtube-nocookie + rel=0 (FR-COM-08)
  desc_child_ko?: string;
  desc_guardian_ko?: string;
  ar_scene?: string;
}

// ── QR/NFC 리다이렉트 — FR-QR ─────────────────────────────────
export interface RedirectEntry {
  code: string; // b03, g01, w, h, e ...
  target: string;
  utm?: { source?: string; medium?: string; content?: string };
}

// ── 실시간(Colyseus) — docs/build/02 §5 (M2) ──────────────────
export type Dir = "up" | "down" | "left" | "right";
export type Motion = "idle" | "walk" | "emote";

export interface MovePayload {
  x: number;
  y: number;
  dir: Dir;
  motion: Motion;
}
export interface ChatOut {
  nickname: string;
  text: string;
  ts: number;
}
export type HostAction = "mute" | "kick" | "notice";

// ── 경주 스탬프 랠리 — FR-RALLY, docs/build/07 ────────────────
export type RallyVerify = "geofence_ar" | "onsite_qr";

export interface RallyPoint {
  id: string;
  title_ko: string;
  lat: number;
  lng: number;
  radius_m: number;
  verify: RallyVerify;
  ar_asset?: string;
}

export interface RallyClaimRequest {
  hero: string; // 히어로 코드(여권)
  pointId: string;
  method: RallyVerify;
  coord?: { lat: number; lng: number; accuracy: number }; // geofence_ar — 검증 후 폐기, 미저장
  qrToken?: string | null; // onsite_qr
}

export type RallyClaimReason =
  | "out_of_range"
  | "low_accuracy"
  | "closed_hours"
  | "event_ended"
  | "already"
  | "too_fast"
  /**
   * 🔴 **저장이 안 됐다.** 「찍혔다」와 반드시 구별해야 한다 —
   * 아이는 지금 그 자리에 서 있고, 찍혔다는 말을 들으면 걸어가 버린다.
   * 나중에 여권을 열면 도장이 없고, 그때는 다시 갈 수가 없다.
   */
  | "save_failed"
  /**
   * 🔴 **이 인터넷에 사람이 너무 많다** — 그 아이 잘못이 아니다.
   *
   * 경주 현장에서는 한 반·한 가족이 **같은 와이파이(또는 통신사 NAT)** 를 쓴다.
   * 서버에서는 전부 **한 주소**로 보인다. 그걸 「너무 빨라요」로 말하면 아이는
   * 자기가 뭘 잘못한 줄 알고, 기다렸다 다시 눌러도 옆 친구들 때문에 또 막힌다.
   * 무엇 때문인지 정확히 말해야 어른이 「잠깐 나눠서 하자」고 할 수 있다.
   */
  | "busy";

export interface RallyClaimResponse {
  stamped: boolean;
  pointId?: string;
  collected: number;
  total: number;
  completed: boolean;
  rank?: number;
  withinWinners?: boolean;
  claimCode?: string;
  claimFormUrl?: string;
  reason?: RallyClaimReason;
}

export const MILESTONES = ["M1", "M2", "M3"] as const;
export type Milestone = (typeof MILESTONES)[number];

// ── 히어로 파츠 카탈로그·검증(클라/서버 공유) — FR-HERO-15 ────
export * from "./hero";
export * from "./heavyArt";

// ── 모자·소품을 룩 위 어디에 놓을지(두 렌더러 공용) — FR-HERO-01 ──
export * from "./contentUpdate";
export * from "./assetUrl";
export * from "./worldMarker";
export * from "./heroAnchor";
export * from "./heroOverlayFit";
export * from "./heroIdle";

// ── QR 리다이렉트 해석(클라/엣지 공유) — FR-QR ────────────────
export * from "./redirect";

// ── 채팅·닉네임 필터(클라/서버 공유) — FR-MULTI-04/S2-2 ────────
export * from "./filter";
export * from "./svgSafe";
export * from "./youtube";
export * from "./bookRecover";
export * from "./storyLook";
export * from "./chunkError";
export * from "./cutout";
export * from "./walkStrip";

// ── 실시간 메시지 프로토콜(클라/서버 공유) — M2 ───────────────
export * from "./protocol";

// ── 행사(이벤트) 일정·무대 — FR-EVENT (M3) ────────────────────
export * from "./events";
export * from "./releaseReady";
export * from "./landingStage";
export * from "./bookOcr";
export * from "./overlayAutoFit";

// ── 경주 스탬프 랠리 — FR-RALLY (위치기반) ────────────────────
export * from "./rally";
export * from "./kstTime";

// ── AI 운영자 NPC 도슨트 — FR-NPC (STEP2) ─────────────────────
export * from "./npc";
export * from "./npcAgent";
export * from "./npcChatLog";
export * from "./aiKb";
export * from "./npcQaDraft";
export * from "./adultGate";
export * from "./npcChatText";

// ── AI 사업: 다국어·RAG·추천 — FR-AI (STEP2) ──────────────────
export * from "./ai";

// ── 관리자 에셋 슬롯·규격 검증 — docs/build/09 (운영) ─────────
export * from "./assetSpec";

// ── 관리자 존 배치 편집(Tiled) — docs/build/09 §6-B ───────────
export * from "./tiledEdit";
// ── 세부 충돌 칸(48px 타일 안을 2~4조각으로) — 렌더 그리드는 그대로 ──
export * from "./collisionFine";
// ── 배경 그림에서 벽 초안 뽑기 — 관리자 버튼과 스크립트가 같은 함수를 쓴다 ──
export * from "./wallScan";

// ── 콘텐츠 와이어 타입(매니페스트·레이아웃) 단일 정의 ──────────
export * from "./content";

// ── 굿즈 상세 — FR-GOODS / docs/build/16 (P1 코어, P2에서 AI 초안이 같은 스키마로 채움) ──
export * from "./goods";

// ── AI 공급자 카탈로그(Claude·GPT·Gemini·Grok) — docs/build/16 §4-5 ──
export * from "./aiProvider";

// ── 굿즈 존 자동 진열 — docs/build/16 §5 P3 ──
export * from "./goodsPlacement";

// ── 굿즈 AI 초안 역할·스키마 — docs/build/16 §4 (순수 조립, SDK 호출은 엣지) ──
export * from "./goodsDraft";

// ── 전자책 페이지 비율(가로:세로) 프리셋·검증 ──
export * from "./bookAspect";
// ── 전자책 표지·목록 메타(웹 고르기 화면·관리자 표지·엣지 /api/books 공유) ──
export * from "./bookMeta";
export * from "./mergeLists";
export * from "./mapLinks";
export * from "./mapShape";
export * from "./meetEmbed";
export * from "./meetProbe";
export * from "./meetHowTo";
export * from "./meetKinds";
export * from "./whaleNeeded";
export * from "./deadHosts";
export * from "./deployStamp";
export * from "./dbFail";
export * from "./bookDub";
export * from "./zipStore";
export * from "./arVideo";
export * from "./arGroup";
export * from "./assetUpload";
export * from "./artDraft";
export * from "./artIdea";
export * from "./spriteBake";
export * from "./heroView";
export * from "./classroom";
export * from "./printCard";
export * from "./classSeat";
export * from "./classStage";
export * from "./eventRoom";
export * from "./story";
export * from "./storyCheer";
export * from "./classBoard";
export * from "./uiFeatures";
export * from "./classRetention";
export * from "./classPlan";

// ── 마을 소식(커뮤니티) — 운영자 발행 읽기 콘텐츠(UGC 아님: 무수집·아동안전) ──
export * from "./notice";

// ── NPC 대사 풀(마을 생활) — 운영자 승인분만 노출, 결정론적 공유 스케줄 ──
export * from "./npcLine";
// ── NPC 대사 AI 초안(운영자 보조) — 결정론적 안전필터 + 전부 pending ──
export * from "./npcLineDraft";
// ── QR/NFC 코드 운영자 편집 — 같은 출처 경로만(오픈 리다이렉트 방지) ──
export * from "./redirectDoc";
// ── 홈 화면 문구(운영자 편집) — 평문만, 비면 기본값 ──
export * from "./siteText";
export * from "./ageBands";
export * from "./worldSize";
export * from "./animSlot";
// ── 관리자 "캐릭터" 화면의 캐릭터 목록 — 카탈로그에서 자동 수집(빠지는 캐릭터 없게) ──
export * from "./charRoster";
// ── 자리표시자(REPLACE·로컬주소) 판별 — 미설정 링크를 아이에게 보이지 않게 ──
export * from "./placeholder";
// ── 존이 실제로 쓰는 그림 수집(관리자 "체험존 그림") ──
export * from "./zoneImages";
// ── 관리자 화면 목록(메뉴·검색·도우미 공용) + 길잡이 도우미 ──
// ── UI 그림(버튼 아이콘·메뉴) 목록 — 운영자가 갈아끼울 수 있게 ──
export * from "./uiArt";
// ── 버튼·강조 색 팔레트(운영자는 "고르기"만 — 대비는 테스트가 지킨다) ──
export * from "./theme";
export * from "./adminScreens";
export * from "./adminAssist";
export * from "./stats";
export * from "./errorStat";

/** 🖨️ 굿즈 인쇄 가능 판정 — 점검 도구·관리자 업로드가 같은 값을 쓴다. */
export * from "./printArt";

/** 📷 AR 자리 이름 — 일곱 화면이 같이 쓴다. */
export * from "./arKeys";
export * from "./arInteract";
export * from "./arTrack";
export * from "./magicStrip";
export * from "./magicPublish";
export * from "./magicHealth";
export * from "./ownerTodo";
export * from "./guardianTable";
export * from "./magicLoss";
export * from "./magicPages";
export * from "./subtitleDiff";
export * from "./ghostAudio";
export * from "./arTilt";
export * from "./arOffer";
export * from "./zoneName";
export * from "./mapStuck";
export * from "./permissionsPolicy";
export * from "./classMeet";
export * from "./stageInApp";
export * from "./inAppEscape";

/** 🏷 자리 이름이 누구 것인가 — 점검 스크립트도 직접 읽는다(import 없음). */
export * from "./assetOwner";
